import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "@/components/ui/chart";
import { UUIDs } from "@/types/uuids";
import dayjs from "dayjs";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import type { EntryViewT } from "../../../types/database/entries";
import {
	convertEntriesToChartConfig,
	convertEntriesToChartData,
	preprocessFaamEntries,
} from "./utils";

export function FaamChart({
	entries,
	formId,
	disableAnimations = false,
}: {
	entries: EntryViewT[];
	formId: string;
	disableAnimations?: boolean;
}) {
	// Preprocess FAAM entries to convert text values to percentages
	const preprocessedEntries = preprocessFaamEntries(entries);
	const chartConfig = convertEntriesToChartConfig(preprocessedEntries, formId);
	const chartData = convertEntriesToChartData(preprocessedEntries, formId);

	// Helper to get original text value and calculate percentage for tooltip display
	const getTooltipValue = (dataKey: string, entryData: { date: string }) => {
		// Find the original entry for this data point
		const originalEntry = entries.find(
			(e) => e.calculated_entry_time === entryData.date,
		);

		if (!originalEntry) return dataKey;

		// Map dataKey to the corresponding input_id
		const inputIdMap: Record<string, string> = {
			"Activities of Daily Living Subscale": UUIDs.faamInputs.adlSubscale,
			"Sports Subscale": UUIDs.faamInputs.sportsSubscale,
			"Total Score": UUIDs.faamInputs.totalScore,
		};

		const targetInputId = inputIdMap[dataKey];
		if (!targetInputId) return dataKey;

		// Find the entry value by input_id
		const entryValue = originalEntry.entry_values?.find(
			(ev) => ev.input_id === targetInputId,
		);

		const textValue = entryValue?.text_value;
		if (typeof textValue === "string" && textValue.includes("/")) {
			const [numerator, denominator] = textValue.split("/");
			const percentage = (
				(Number(numerator) / Number(denominator)) *
				100
			).toFixed(1);
			return `${textValue} = ${percentage}%`;
		}

		return textValue || dataKey;
	};

	return (
		<ChartContainer config={chartConfig}>
			<LineChart accessibilityLayer data={chartData}>
				<CartesianGrid vertical={false} />
				<XAxis
					dataKey="date"
					tickLine={false}
					tickMargin={10}
					axisLine={false}
					tickFormatter={(value) => {
						return dayjs(value).format("DD MMM");
					}}
				/>
				<YAxis
					domain={[0, 100]}
					tickLine={false}
					axisLine={false}
					tickMargin={10}
					label={{
						value: "FAAM Score (%)",
						angle: -90,
						position: "insideLeft",
					}}
				/>
				<ChartTooltip
					content={
						<ChartTooltipContent
							hideLabel
							className="p-3 w-[200px] md:w-[300px]"
							formatter={(value, name, item, index) => (
								<div className="flex flex-col gap-2">
									{index === 0 && (
										<>
											<div className="flex flex-col">
												<span className="mb-1 text-[0.65rem] text-slate-400 uppercase tracking-wider">
													Timing
												</span>
												<span className="font-medium text-slate-700 text-sm">
													{item.payload.timing}
												</span>
												<div className="mt-1 text-slate-600 text-xs">
													{dayjs(item.payload.date).format("DD MMM YYYY")}
												</div>
											</div>

											<div className="flex flex-col bg-slate-50 shadow-sm p-2 border border-slate-200 rounded-md">
												<span className="mb-1 font-bold text-[0.65rem] text-slate-500 uppercase tracking-wider">
													Foot and Ankle Ability Measure
												</span>
												<span className="flex items-center font-semibold text-slate-900 text-lg">
													<span className="text-indigo-600">
														{getTooltipValue("Total Score", item.payload)}
													</span>
												</span>
											</div>
											<div className="flex flex-col gap-1.5 pt-1 border-slate-100 border-t">
												<div className="flex flex-col">
													<span className="mb-1 text-[0.65rem] text-slate-600 uppercase tracking-wider">
														Activities of Daily Living
													</span>
													<span className="font-medium text-slate-700 text-sm">
														<span className="bg-[hsl(var(--chart-1))]/10 px-1.5 py-0.5 rounded text-slate-900">
															{getTooltipValue(
																"Activities of Daily Living Subscale",
																item.payload,
															)}
														</span>
													</span>
												</div>
												<div className="flex flex-col">
													<span className="mb-1 text-[0.65rem] text-slate-600 uppercase tracking-wider">
														Sports Activities
													</span>
													<span className="font-medium text-slate-700 text-sm">
														<span className="bg-[hsl(var(--chart-2))]/10 px-1.5 py-0.5 rounded text-slate-900">
															{getTooltipValue("Sports Subscale", item.payload)}
														</span>
													</span>
												</div>
											</div>
										</>
									)}
								</div>
							)}
						/>
					}
					cursor={false}
				/>
				<Line
					isAnimationActive={!disableAnimations}
					type="natural"
					dataKey="Activities of Daily Living Subscale"
					stroke="hsl(var(--chart-1))"
					strokeWidth={2}
					dot={{ fill: "hsl(var(--chart-1))", strokeWidth: 2, r: 4 }}
				/>
				<Line
					isAnimationActive={!disableAnimations}
					type="natural"
					dataKey="Sports Subscale"
					stroke="hsl(var(--chart-2))"
					strokeWidth={2}
					dot={{ fill: "hsl(var(--chart-2))", strokeWidth: 2, r: 4 }}
				/>
				<Line
					isAnimationActive={!disableAnimations}
					type="natural"
					dataKey="Total Score"
					stroke="hsl(var(--chart-3))"
					strokeWidth={2}
					dot={{ fill: "hsl(var(--chart-3))", strokeWidth: 2, r: 4 }}
				/>
			</LineChart>
		</ChartContainer>
	);
}
